import React from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import Tooltip from '@atlaskit/tooltip';
import { type IntlShape, useIntl } from '@atlassian/jira-intl';
import type { AssociatedField, OptionType } from '../../../../../types/layout-items.tsx';
import { DefaultValueLabel } from '../../field-options/option/view.tsx';
import messages from './messages.tsx';

export type Props = {
	readonly isSelected: boolean;
	readonly isOptionHovered: boolean;
	readonly isOptionFocused: boolean;
	readonly field: AssociatedField;
	readonly option: OptionType;
	readonly onFieldUpdated: (field: AssociatedField) => void;
	readonly tooltipDelay?: number;
	readonly testId?: string;
};

const onDefaultValueChange =
	(
		field: AssociatedField,
		onFieldUpdated: (arg1: AssociatedField) => unknown,
		defaultValue?: OptionType,
	) =>
	() => {
		onFieldUpdated({
			...field,
			defaultValue,
		});
	};

export const DefaultValueControlRight = ({
	field,
	field: {
		operations: { canModifyDefaultValue },
	},
	option,
	isSelected,
	isOptionHovered,
	isOptionFocused,
	onFieldUpdated,
	tooltipDelay,
	testId,
}: Props) => {
	const intl: IntlShape = useIntl();
	const interactingWithOption = isOptionHovered || isOptionFocused;

	// If isModifyDefaultValueDisabled is true then we never show the button to change default value.
	// Otherwise, we show the button in a hidden state, and make it visible when the option is hovered or focused.
	const changeDefaultValueFragment = !canModifyDefaultValue ? null : (
		<Wrapper isActive={interactingWithOption}>
			<Button
				appearance="link"
				isDisabled={!canModifyDefaultValue}
				onClick={onDefaultValueChange(field, onFieldUpdated, isSelected ? undefined : option)}
			>
				{intl.formatMessage(isSelected ? messages.clearDefault : messages.setAsDefault)}
			</Button>
		</Wrapper>
	);

	// We only show the label if the option is selected AND the change default value button is not visible.
	const defaultLabelFragment =
		!isSelected || (canModifyDefaultValue && interactingWithOption) ? null : (
			<Tooltip
				content={
					!canModifyDefaultValue ? intl.formatMessage(messages.disabledDefaultTooltip) : null
				}
				delay={tooltipDelay}
			>
				<DefaultValueLabel isDisabled={!canModifyDefaultValue} data-testid={testId}>
					{intl.formatMessage(messages.default)}
				</DefaultValueLabel>
			</Tooltip>
		);

	return (
		<>
			{changeDefaultValueFragment}
			{defaultLabelFragment}
		</>
	);
};

type StyleProps = {
	isActive: boolean;
};

/**
 * Hides the Button visually but still makes it accessible and focusable via keyboard.
 *
 * If you think it is dirty that's because it is.
 * Feel free to make it more beautiful, but make sure to test these cases:
 *
 * - Hovering over the whole Field card makes the button appear
 * - <tab> focuses the button even when it was hidden before the focus
 * - When the parent field component
 * - When focused with tab-tab, pressing either <space> or <enter> triggers the 'onDeleteClick' action
 *
 * Inspired by https://webaim.org/techniques/css/invisiblecontent/ and https://webaim.org/techniques/skipnav/
 */

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div<StyleProps>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& button': {
		left: '-10000px',
		top: 'auto',
		overflow: 'hidden',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		position: ({ isActive }: StyleProps) => (isActive ? 'static' : 'absolute'),
	},
});
